import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import DataTable from '../DataTable/DataTable';
import { fetchProducts, createProduct, editProduct, removeProduct } from '../../redux/services/productsService';
import { fetchCategories } from '../../redux/services/categoriesService';
import { fetchBrands } from '../../redux/services/brandsService';
import { fetchEvents } from '../../redux/services/eventService';
import VariantBuilder, { renderVariantLabel } from './VariantBuilder'; // ← make sure VariantBuilder.jsx is in the same folder
import { confirmDelete } from '../../utils/sweetalert';
import { hasPermission } from '../../utils/authHelper';
import API from '../../api/axiosInstance';
import AccessDenied from '../AccessDenied';

const BASE_URL = process.env.REACT_APP_API_URL;
const IMG_URL = process.env.REACT_APP_IMG_URL;

// Product Image Dimension Validator (800×960px 5:6 portrait)
const PRODUCT_IMAGE_DIMENSIONS = {
  width: 800,
  height: 960,
  aspectRatio: 5 / 6,
  tolerance: 0.05,
  maxFileSize: 3 * 1024 * 1024,
  formats: [
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
    'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon',
    'image/heic', 'image/heif', 'image/avif'
  ],
};

const validateProductImageDimensions = (file) => {
  return new Promise((resolve) => {
    // Check file size
    // if (file.size > PRODUCT_IMAGE_DIMENSIONS.maxFileSize) {
    //   resolve({
    //     valid: false,
    //     error: `File too large. Max: 3MB. You have: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    //   });
    //   return;
    // }

    // Check file format
    if (!PRODUCT_IMAGE_DIMENSIONS.formats.includes(file.type)) {
      resolve({
        valid: false,
        error: `Invalid format. Use common image formats (JPG, PNG, WebP, GIF, SVG, BMP, TIFF, ICO, HEIC, HEIF, AVIF). You have: ${file.type || 'unknown'}`,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const actualRatio = width / height;
        const expectedRatio = PRODUCT_IMAGE_DIMENSIONS.aspectRatio;
        const ratioDiff = Math.abs(actualRatio - expectedRatio) / expectedRatio;

        // if (ratioDiff > PRODUCT_IMAGE_DIMENSIONS.tolerance) {
        //   resolve({
        //     valid: false,
        //     error: `Incorrect aspect ratio. Use 5:6 portrait (${PRODUCT_IMAGE_DIMENSIONS.width}×${PRODUCT_IMAGE_DIMENSIONS.height}px). Yours: ${width}×${height}px`,
        //     dimensions: { width, height },
        //   });
        //   return;
        // }

        resolve({
          valid: true,
          dimensions: { width, height },
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // Handle stringified array: "[\"uploads/products/123.jpeg\",...]"
  if (typeof imagePath === 'string' && imagePath.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(imagePath);
      imagePath = Array.isArray(parsed) ? parsed[0] : imagePath;
    } catch { /* fall through */ }
  }
  if (Array.isArray(imagePath)) imagePath = imagePath[0];
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const filename = imagePath.replace(/^\//, '').replace(/^uploads\//, '');
  return `${IMG_URL}/uploads/${filename}`;
};

// Convert a full URL back to the relative path the server stores (uploads/products/file.jpg)
const toRelativePath = (url) => {
  if (!url || url.startsWith('blob:')) return null;
  if (url.startsWith('http')) {
    // Strip protocol + host, then normalise leading slash
    try {
      const pathname = new URL(url).pathname; // e.g. /uploads/products/abc.jpg
      return pathname.replace(/^\//, ''); // → uploads/products/abc.jpg
    } catch { /* fall through */ }
  }
  // Already relative
  return url.replace(/^\//, '');
};
const KM = {
  orange: '#F15A24', orangeLight: '#FEF0EB', blue: '#1A3A6B',
  green: '#39B54A', teal: '#00B4D8', border: '#E5E7EB',
  text: '#1A1A2E', muted: '#6B7280', bg: '#F9FAFB',
};

// ── Styles ────────────────────────────────────────────────────────────────────
const formCard = { background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, marginBottom: 20 };
const formHeader = { background: KM.blue, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10, borderTopLeftRadius: 11, borderTopRightRadius: 11 };
const headerIcon = { width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 600 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelStyle = { fontSize: 11, fontWeight: 500, color: KM.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { padding: '9px 12px', border: `1px solid ${KM.border}`, borderRadius: 8, fontSize: 13, color: KM.text, background: '#fff', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const submitBtn = { gridColumn: 'span 2', padding: 11, background: KM.orange, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 };
const tag = (color, bg) => ({ fontSize: 10, fontWeight: 700, color, background: bg, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' });
const errorStyle = { fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 };

// stockColor is defined inside Products component using live inventory settings
function totalStock(p) { return p.Variants?.reduce((a, v) => a + Number(v.stock || 0), 0) ?? 0; }

// ── Blank variant (matches VariantBuilder's internal shape) ───────────────────
function blankVariantRow() {
  return { id: Date.now() + Math.random(), mrp: '', salesPrice: '', stock: '', attributes: [{ key: '', value: '', customValue: '' }], imageFiles: [], imagePreviews: [] };
}

// ── Form defaults ─────────────────────────────────────────────────────────────
const BLANK_FORM = {
  productName: '', categoryId: '', categoryName: '', subCategoryId: '', subCategoryName: '',
  brandId: '', discount: '', shortDescription: '', fullDescription: '',
  tag: '', isCustomisable: false, isNewArrival: false, isHotDeal: false,
  isPartialCodAvailable: false,
  customisationFields: {},
  shippingWeight: '', shippingLength: '', shippingBreadth: '', shippingHeight: '',
  gstRate: '',
};

// ── EventTagSelector — searchable multi-tag picker from Events model ──────────
function EventTagSelector({ value, onChange }) {
  const { items: events } = useSelector(s => s.events || {});
  const [inputVal, setInputVal] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef();

  const selectedTags = value ? value.split(',').map(t => t.trim()).filter(Boolean) : [];

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allEventLabels = (events || [])
    .filter(e => e.isActive !== false)
    .map(e => (e.value || e.label || '').toLowerCase().trim())
    .filter(Boolean);

  const filteredSuggestions = allEventLabels.filter(lbl =>
    lbl.includes(inputVal.toLowerCase()) && !selectedTags.map(t => t.toLowerCase()).includes(lbl)
  );

  const addTag = (tag) => {
    const t = tag.trim();
    if (!t || selectedTags.map(x => x.toLowerCase()).includes(t.toLowerCase())) return;
    onChange([...selectedTags, t].join(', '));
    setInputVal('');
    setOpen(false);
  };

  const removeTag = (idx) => {
    onChange(selectedTags.filter((_, i) => i !== idx).join(', '));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputVal.trim()) {
      e.preventDefault();
      addTag(inputVal);
    }
    if (e.key === 'Backspace' && !inputVal && selectedTags.length) removeTag(selectedTags.length - 1);
    if (e.key === 'Escape') setOpen(false);
  };

  const blueFaint = '#EEF2FB';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          padding: '7px 10px', border: `1px solid ${open ? KM.teal : KM.border}`,
          borderRadius: 8, background: '#fff', minHeight: 40, cursor: 'text',
        }}
      >
        {selectedTags.map((tag, i) => (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: blueFaint, color: KM.blue, border: '1px solid #B8C9EE',
            borderRadius: 5, fontSize: 12, fontWeight: 600, padding: '2px 8px',
          }}>
            {tag}
            <span onClick={(e) => { e.stopPropagation(); removeTag(i); }}
              style={{ cursor: 'pointer', color: KM.muted, fontSize: 14, lineHeight: 1 }}>×</span>
          </span>
        ))}
        <input
          style={{ border: 'none', outline: 'none', padding: '3px 4px', fontSize: 13, background: 'transparent', fontFamily: 'inherit', minWidth: 140, flex: 1, color: KM.text }}
          value={inputVal}
          placeholder={selectedTags.length ? '' : 'Search or type event tag…'}
          onChange={e => { setInputVal(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', marginTop: 4, maxHeight: 200, overflowY: 'auto',
        }}>
          {filteredSuggestions.map(lbl => (
            <div key={lbl} onMouseDown={(e) => { e.preventDefault(); addTag(lbl); }}
              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = KM.bg}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span>🏷️</span> {lbl}
            </div>
          ))}
          {inputVal.trim() && !allEventLabels.includes(inputVal.trim().toLowerCase()) && (
            <div onMouseDown={(e) => { e.preventDefault(); addTag(inputVal.trim()); }}
              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: KM.orange, fontWeight: 600, borderTop: filteredSuggestions.length ? `1px solid ${KM.border}` : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = KM.orangeLight}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              + Add "{inputVal.trim()}" as new tag
            </div>
          )}
          {!filteredSuggestions.length && !inputVal.trim() && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: KM.muted }}>Type to search or create a tag…</div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function Products({ showToast }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { items: allProducts, loading } = useSelector(s => s.products || {});
  const { items: rawCategories } = useSelector(s => s.categories || {});
  const { items: brands } = useSelector(s => s.brands || {});
  const { settings: invSettings } = useSelector(s => s.notifications);
  const stockColor = (q) => {
    const high   = invSettings?.highStockThreshold   ?? 51;
    const medium = invSettings?.mediumStockThreshold  ?? 11;
    if (q === 0)    return '#EF4444';
    if (q < medium) return '#EF4444';
    if (q < high)   return '#F59E0B';
    return '#39B54A';
  };

  // Safe checks: ensure categories and brands are normalized arrays to prevent runtime crashes
  const categories = Array.isArray(rawCategories) ? rawCategories : [];
  const safeBrands = Array.isArray(brands) ? brands : [];

  // ── Core state ────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...BLANK_FORM });
  const [errors, setErrors] = useState({});
  const [customisationFieldsTemplates, setCustomisationFieldsTemplates] = useState([]);

  // ── Variants — now managed by VariantBuilder ──────────────────────────────
  const [variants, setVariants] = useState([blankVariantRow()]);
  const [variantTab, setVariantTab] = useState('options'); // 'options' | 'skus'

  // ── Multi-image state ─────────────────────────────────────────────────────
  const [imageFiles, setImageFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [imageDimensionsMap, setImageDimensionsMap] = useState({});
  const [originalPreviews, setOriginalPreviews] = useState([]); // snapshot on edit open
  const fileInputRef = useRef();
  const variantBuilderRef = useRef();

  // ── SubCategories derived from selected category safely ───────────────────
  const selectedCategory = categories.find(c => String(c.id) === String(formData.categoryId));
  const productCategories = categories.filter(c => c.value !== null && c.value !== undefined && c.value !== '');

  const subCategories = selectedCategory?.subcategories || selectedCategory?.subCategories || selectedCategory?.SubCategories || [];

  // ── Scroll to VariantBuilder when tab forced to 'skus' on validation error ──
  useEffect(() => {
    if (variantTab === 'skus' && errors.variantErrors) {
      variantBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [variantTab, errors.variantErrors]);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchBrands());
    dispatch(fetchEvents());
    dispatch(fetchProducts());

    API.get('/customisation-fields?active=true')
      .then(res => {
        setCustomisationFieldsTemplates(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        console.error("Failed to load customisation fields", err);
      });
  }, [location.search]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const isLowStock = new URLSearchParams(location.search).get('filter') === 'lowstock';
  const rows = isLowStock
    ? [...(allProducts || [])].filter(p => totalStock(p) < 50).sort((a, b) => totalStock(a) - totalStock(b))
    : (allProducts || []);

  if (!hasPermission('products_view')) {
    return <AccessDenied moduleName="Products" />;
  }

  // ── Image handlers ────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    
    const currentLength = imageFiles.length;
    
    // Validate each file and add to state
    selected.forEach((file, idx) => {
      validateProductImageDimensions(file).then((result) => {
        setImageDimensionsMap(prev => ({
          ...prev,
          [currentLength + idx]: result,
        }));
      });
    });
    
    setImageFiles(prev => [...prev, ...selected]);
    setPreviews(prev => [...prev, ...selected.map(f => URL.createObjectURL(f))]);
    e.target.value = null;
  };

  const removeImage = (index) => {
    if (previews[index]?.startsWith('blob:')) URL.revokeObjectURL(previews[index]);
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    
    // Rebuild imageDimensionsMap with updated indices
    setImageDimensionsMap(prev => {
      const newMap = {};
      Object.entries(prev).forEach(([idx, val]) => {
        const idxNum = parseInt(idx, 10);
        if (idxNum < index) {
          newMap[idxNum] = val;
        } else if (idxNum > index) {
          newMap[idxNum - 1] = val;
        }
      });
      return newMap;
    });
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const reset = () => {
    previews.forEach(url => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); });
    setEditingId(null);
    setShowForm(false);
    setFormData({ ...BLANK_FORM });
    setErrors({});
    setVariants([blankVariantRow()]);
    setVariantTab('options');
    setImageFiles([]);
    setPreviews([]);
    setImageDimensionsMap({});
    setOriginalPreviews([]);
  };

  // Cancel edit — restore original server images, discard local changes
  const handleCancel = () => {
    previews.forEach(url => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); });
    if (editingId) {
      // Restore snapshot taken when edit opened
      setPreviews([...originalPreviews]);
      setImageFiles([]);
      setImageDimensionsMap({});
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...BLANK_FORM });
      setErrors({});
      setVariants([blankVariantRow()]);
      setVariantTab('options');
      setOriginalPreviews([]);
    } else {
      reset();
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (p) => {
    const catName = p.Category?.name || p.Category?.label || '';
    const subCatId = p.subCategoryId || p.SubCategory?.id || '';
    const subCatName = p.SubCategory?.name || p.SubCategory?.label || '';

    setEditingId(p.id);
    setFormData({
      productName: p.name || '',
      categoryId: p.categoryId || '',
      categoryName: catName,
      subCategoryId: subCatId,
      subCategoryName: subCatName,
      brandId: p.brandId || '',
      discount: p.discount ?? '',
      shortDescription: p.shortDescription || '',
      fullDescription: p.fullDescription || '',
      tag: Array.isArray(p.tag) ? p.tag.join(', ') : (p.tag || ''),
      isCustomisable: p.isCustomisable !== false,
      isNewArrival: !!p.isNew,
      isHotDeal: !!p.isHotDeal,
      isPartialCodAvailable: p.isPartialCodAvailable !== false,
      customisationFields: (() => {
        let cf = p.customisationFields || {};
        if (typeof cf === 'string') {
          try { cf = JSON.parse(cf); } catch { cf = {}; }
        }
        const mapped = {};
        if (cf && typeof cf === 'object') {
          Object.keys(cf).forEach(k => {
            mapped[k] = !!cf[k];
          });
        }
        return mapped;
      })(),
      shippingWeight: p.shippingWeight || '',
      shippingLength: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return sd?.length || '';
      })(),
      shippingBreadth: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return sd?.breadth || '';
      })(),
      shippingHeight: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return sd?.height || '';
      })(),
      gstRate: p.gstRate !== undefined && p.gstRate !== null ? String(p.gstRate) : '',
    });

    // ── Map existing variants → VariantBuilder shape ──────────────────────
    setVariants(
      p.Variants?.length
        ? p.Variants.map(v => {
          const parseVariantName = (name) => {
            if (!name || name === 'Default') return [];
            return name.split(/\s*(?:·|\||,|\/|-)\s*/).map(part => {
              const ci = part.indexOf(':');
              if (ci === -1) return { key: 'Custom Note', value: part.trim(), customValue: '' };
              return { key: part.slice(0, ci).trim(), value: part.slice(ci + 1).trim(), customValue: '' };
            }).filter(a => a.key && a.value);
          };

          const nameAttrs = parseVariantName(v.variantName);
          const rawAttrs = v.attributes;
          const parsedAttrs = typeof rawAttrs === 'string'
            ? (() => { try { return JSON.parse(rawAttrs); } catch { return []; } })()
            : (Array.isArray(rawAttrs) ? rawAttrs : []);

          // HEAL LOGIC: If parsed attributes from variantName do not match DB attributes,
          // automatically heal DB attributes to match the name-based ones.
          let finalAttrs = parsedAttrs;
          if (nameAttrs.length > 0) {
            const matches = nameAttrs.every(na =>
              parsedAttrs.some(pa =>
                na.key.toLowerCase() === pa.key.toLowerCase() &&
                na.value.toLowerCase() === pa.value.toLowerCase()
              )
            );
            if (!matches || parsedAttrs.length !== nameAttrs.length) {
              finalAttrs = nameAttrs;
            }
          }

          const builtAttrs = finalAttrs.length > 0
            ? finalAttrs.map(a => ({ key: a.key, value: a.value, customValue: '' }))
            : (() => {
              const fromFlat = [
                v.colour ? { key: 'Colour', value: v.colour, customValue: '' } : null,
                v.size ? { key: 'Size', value: v.size, customValue: '' } : null,
                v.material ? { key: 'Material', value: v.material, customValue: '' } : null,
                v.finish ? { key: 'Finish', value: v.finish, customValue: '' } : null,
                v.weight ? { key: 'Weight', value: v.weight, customValue: '' } : null,
                v.dimensions ? { key: 'Dimensions', value: v.dimensions, customValue: '' } : null,
                v.engraving ? { key: 'Engraving', value: v.engraving, customValue: '' } : null,
                v.printText ? { key: 'Print Text', value: v.printText, customValue: '' } : null,
                v.customLabel ? { key: 'Custom Note', value: v.customLabel, customValue: '' } : null,
              ].filter(Boolean);
              return fromFlat.length > 0 ? fromFlat : nameAttrs;
            })();

          return {
            id: v.id || Date.now() + Math.random(),
            open: false,
            mrp: v.mrp || '',
            salesPrice: v.salesPrice || '',
            stock: v.stock ?? '',
            status: v.status || 'Active',
            imageFiles: [],
            imagePreviews: (() => {
              const imgs = Array.isArray(v.image) 
                ? v.image 
                : typeof v.image === 'string' && v.image.trim().startsWith('[') 
                  ? (() => { try { return JSON.parse(v.image); } catch { return [v.image]; } })()
                  : v.image ? [v.image] : [];
              return imgs.map(img => getImageUrl(img)).filter(Boolean);
            })(),
            combo: builtAttrs.map(a => ({ key: a.key, value: a.value })),
            variantName: v.variantName || builtAttrs.map(a => `${a.key}: ${a.value}`).join(' · '),
            attributes: builtAttrs.length > 0 ? builtAttrs : [{ key: '', value: '', customValue: '' }],
          };
        })
        : [blankVariantRow()]
    );

    const imgs = p.images || (p.image ? (Array.isArray(p.image) ? p.image : [p.image]) : []);
    const mappedImgs = imgs.map(img => getImageUrl(img));
    setPreviews(mappedImgs);
    setOriginalPreviews(mappedImgs); // snapshot for cancel
    setImageFiles([]);
    setImageDimensionsMap({});
    setErrors({});
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFirstInvalidImageError = () =>
    Object.values(imageDimensionsMap).find(result => result && !result.valid)?.error;

  const ErrorMsg = ({ field }) => {
    const message = field === 'images'
      ? (errors[field] || getFirstInvalidImageError())
      : errors[field];

    return message ? <span style={errorStyle}>{field === 'images' ? `⚠ ${message}` : message}</span> : null;
  };

  const countWords = (text) => {
    return text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  };

  const DESC_LIMITS = {
    short: { maxWords: 30, maxChars: 200 },
    long:  { minWords: 10, maxChars: 4000 },
  };

  const validateProduct = () => {
    const next = {};
    const discount = Number(formData.discount || 0);
    const variantErrors = [];

    if (!formData.productName.trim()) next.productName = 'Product Name is required';
    if (!formData.categoryId) next.categoryId = 'Please select a category';
    if (Number.isNaN(discount) || discount < 0 || discount > 100) next.discount = 'Discount must be between 0 and 100';
    if (!variants.length) next.variants = 'Please add at least one variant';

    // Description word count validation
    const shortWordCount = countWords(formData.shortDescription);
    if (formData.shortDescription && shortWordCount > DESC_LIMITS.short.maxWords) {
      next.shortDescription = `Short description must be maximum ${DESC_LIMITS.short.maxWords} words (currently ${shortWordCount}).`;
    }

    const longWordCount = countWords(formData.fullDescription);
    if (formData.fullDescription && longWordCount < DESC_LIMITS.long.minWords) {
      next.fullDescription = `Long description must be at least ${DESC_LIMITS.long.minWords} words (currently ${longWordCount}).`;
    }

    variants.forEach((v, index) => {
      const messages = [];
      const mrp = Number(v.mrp);
      const salesPrice = Number(v.salesPrice);
      const stock = Number(v.stock);
      const hasAttribute = v.attributes?.some(a => a.key && (a.value || a.customValue));

      if (!hasAttribute) messages.push('Add at least one attribute');
      if (!v.mrp) messages.push('Enter MRP');
      else if (Number.isNaN(mrp) || mrp <= 0) messages.push('MRP must be greater than 0');
      if (!v.salesPrice) messages.push('Enter sales price');
      else if (Number.isNaN(salesPrice) || salesPrice <= 0) messages.push('Sales price must be greater than 0');
      else if (!Number.isNaN(mrp) && mrp > 0 && salesPrice > mrp) messages.push('Sales price cannot be greater than MRP');
      if (v.stock === '') messages.push('Enter stock');
      else if (Number.isNaN(stock) || stock < 0) messages.push('Stock cannot be negative');

      if (!v.imagePreviews?.length && !v.imageFiles?.length) messages.push('Upload a variant image');

      variantErrors[index] = messages;
    });

    if (variantErrors.some(list => list.length)) {
      next.variantErrors = variantErrors;
      setVariantTab('skus');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateProduct()) return;
    const fd = new FormData();
    fd.append('productName', formData.productName);
    fd.append('categoryId', formData.categoryId);
    fd.append('brandId', formData.brandId);
    fd.append('discount', formData.discount || 0);
    fd.append('gstRate', formData.gstRate !== '' ? formData.gstRate : 0);
    fd.append('shortDescription', formData.shortDescription);
    fd.append('fullDescription', formData.fullDescription);
    fd.append('isNewArrival', formData.isNewArrival);
    fd.append('isCustomisable', formData.isCustomisable);
    fd.append('isHotDeal', formData.isHotDeal);
    fd.append('isPartialCodAvailable', formData.isPartialCodAvailable);
    fd.append('shippingWeight', formData.shippingWeight || '');
    fd.append('shippingDimensions', JSON.stringify({
      length: formData.shippingLength ? parseFloat(formData.shippingLength) : null,
      breadth: formData.shippingBreadth ? parseFloat(formData.shippingBreadth) : null,
      height: formData.shippingHeight ? parseFloat(formData.shippingHeight) : null
    }));
    if (formData.isCustomisable) {
      fd.append('customisationFields', JSON.stringify(formData.customisationFields));
    }

    if (formData.subCategoryId) fd.append('subCategoryId', formData.subCategoryId);
    if (formData.subCategoryName) fd.append('subCategoryName', formData.subCategoryName);

    const baseTags = formData.tag ? formData.tag.split(',').map(t => t.trim()).filter(Boolean) : [];
    if (formData.isCustomisable && !baseTags.includes('customisable')) baseTags.push('customisable');
    if (formData.isNewArrival   && !baseTags.includes('new-arrival'))  baseTags.push('new-arrival');
    if (formData.isHotDeal      && !baseTags.includes('hot-deal'))      baseTags.push('hot-deal');
    fd.append('tag', JSON.stringify(baseTags));

    // ── Map VariantBuilder state → API payload ────────────────────────────
    const mappedVariants = variants.map((v, idx) => {
      const variantName = v.attributes
        .map(a => {
          const val = a.value === 'Custom' ? (a.customValue || 'Custom') : a.value;
          return val ? (a.key ? `${a.key}: ${val}` : val) : '';
        })
        .filter(Boolean)
        .join(' · ') || 'Default';

      const attributes = v.attributes
        .filter(a => a.key && (a.value || a.customValue))
        .map(a => ({
          key: a.key,
          value: a.value === 'Custom' ? (a.customValue || 'Custom') : a.value,
        }));

      const findAttr = (key) => {
        const found = attributes.find(a => a.key === key);
        return found ? found.value : '';
      };

      return {
        variantName,
        attributes,
        mrp: v.mrp,
        salesPrice: v.salesPrice,
        stock: v.stock,
        unit: findAttr('Size') || findAttr('Capacity') || 'Free Size',
        colour: findAttr('Colour'),
        size: findAttr('Size') || findAttr('Capacity'),
        material: findAttr('Material'),
        finish: findAttr('Finish'),
        weight: findAttr('Weight'),
        dimensions: findAttr('Dimensions'),
        engraving: findAttr('Engraving'),
        printText: findAttr('Print Text'),
        customLabel: findAttr('Custom Note'),
        subCategory: findAttr('Sub-type'),
        existingImages: (v.imagePreviews || [])
          .filter(p => p && !p.startsWith('blob:'))
          .map(p => toRelativePath(p)),
        status: v.status || 'Active',
      };
    });

    fd.append('variants', JSON.stringify(mappedVariants));

    variants.forEach((v, idx) => {
      if (v.imageFiles && v.imageFiles.length > 0) {
        v.imageFiles.forEach(file => {
          fd.append(`variantImages_${idx}`, file);
        });
      }
    });

    imageFiles.forEach(file => fd.append('images', file));
    const savedImages = previews
      .filter(url => url && !url.startsWith('blob:'))
      .map(url => toRelativePath(url))
      .filter(Boolean);
    fd.append('existingImages', JSON.stringify(savedImages));

    const tid = showToast.loading(editingId ? 'Updating…' : 'Adding…');
    try {
      if (editingId) {
        await dispatch(editProduct({ id: editingId, formData: fd }));
        showToast.success('Updated!', tid);
      } else {
        await dispatch(createProduct(fd));
        showToast.success('Added!', tid);
      }
      reset();
      dispatch(fetchProducts());
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Operation failed', tid);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    confirmDelete({
      title: 'Delete Product?',
      message: 'Are you sure you want to delete this product?',
      onConfirm: async () => {
        try {
          await dispatch(removeProduct(id));
        } catch (err) {
          console.error('Failed to delete product:', err);
        }
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="section-title">Products Management</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isLowStock && (
            <button className="action-btn" onClick={() => window.location.href = '/products'}
              style={{ background: '#eee', color: '#333' }}>Clear Filter</button>
          )}
          {showForm ? (
            <button className="action-btn btn-edit" onClick={handleCancel}>
              Close
            </button>
          ) : (
            hasPermission('products_create') && (
              <button className="action-btn btn-edit" onClick={() => setShowForm(true)}>
                + Add Product
              </button>
            )
          )}
        </div>
      </div>

      {showForm && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>🎁</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>
                {editingId ? 'Edit Product' : 'Add New Product'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                Kamali Gift Factory — product details &amp; variants
              </div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Product Name *</label>
                <input style={inputStyle} required value={formData.productName}
                  onChange={e => setFormData({ ...formData, productName: e.target.value })}
                  placeholder="e.g. Personalised Bracelet, Return Gift Hamper" />
                <ErrorMsg field="productName" />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={formData.categoryId}
                  onChange={e => {
                    const cat = productCategories.find(c => String(c.id) === String(e.target.value));
                    setFormData({
                      ...formData,
                      categoryId: e.target.value,
                      categoryName: cat?.name || cat?.label || '',
                      subCategoryId: '',
                      subCategoryName: '',
                    });
                  }}>
                  <option value="">Select Category</option>
                  {productCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name || c.label}</option>
                  ))}
                </select>
                <ErrorMsg field="categoryId" />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Sub-Category
                  {subCategories.length === 0 && formData.categoryId && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: KM.muted, textTransform: 'none', fontWeight: 400 }}>
                      (none for this category)
                    </span>
                  )}
                </label>
                {subCategories.length > 0 ? (
                  <select
                    style={inputStyle}
                    value={formData.subCategoryId}
                    onChange={e => {
                      const sub = subCategories.find(s => String(s.id) === String(e.target.value));
                      setFormData({
                        ...formData,
                        subCategoryId: e.target.value,
                        subCategoryName: sub?.label || sub?.name || '',
                      });
                    }}
                  >
                    <option value="">Select Sub-Category</option>
                    {subCategories.map(s => (
                      <option key={s.id} value={s.id}>{s.label || s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    style={{
                      ...inputStyle,
                      background: formData.categoryId ? '#fff' : KM.bg,
                      color: formData.categoryId ? KM.text : KM.muted,
                    }}
                    placeholder={formData.categoryId ? 'Type sub-category (optional)' : 'Select a category first'}
                    disabled={!formData.categoryId}
                    value={formData.subCategoryName}
                    onChange={e => setFormData({ ...formData, subCategoryName: e.target.value, subCategoryId: '' })}
                  />
                )}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Brand (Optional)</label>
                <select style={inputStyle} value={formData.brandId}
                  onChange={e => setFormData({ ...formData, brandId: e.target.value })}>
                  <option value="">Select Brand</option>
                  {safeBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              <div style={{ ...fieldStyle }}>
                <label style={labelStyle}>Event Tags</label>
                <EventTagSelector
                  value={formData.tag}
                  onChange={val => setFormData({ ...formData, tag: val })}
                />
                <span style={{ fontSize: 11, color: KM.muted, marginTop: 3 }}>
                  Search from events or type a new tag and press Enter
                </span>
              </div>

              {/* ── Short Description ── */}
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Short Description <span style={{ color: KM.muted, textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(max {DESC_LIMITS.short.maxWords} words)</span></span>
                  <span style={{ fontSize: 11, color: (() => { const w = countWords(formData.shortDescription); return (w > DESC_LIMITS.short.maxWords) ? '#ef4444' : '#9ca3af'; })(), fontWeight: 500, textTransform: 'none' }}>
                    {formData.shortDescription.length}/{DESC_LIMITS.short.maxChars} chars · {countWords(formData.shortDescription)}/{DESC_LIMITS.short.maxWords} words
                  </span>
                </label>
                <input
                  style={{
                    ...inputStyle,
                    borderColor: errors.shortDescription ? '#ef4444' : undefined,
                    boxShadow: errors.shortDescription ? '0 0 0 2px rgba(239,68,68,0.1)' : undefined,
                  }}
                  value={formData.shortDescription}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData({ ...formData, shortDescription: val });
                    const wc = countWords(val);
                    if (val && wc > DESC_LIMITS.short.maxWords) {
                      setErrors(prev => ({ ...prev, shortDescription: `Short description must be maximum ${DESC_LIMITS.short.maxWords} words (currently ${wc})` }));
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.shortDescription; return n; });
                    }
                  }}
                  placeholder="e.g. Handcrafted crochet gift box with personalised name tag"
                  maxLength={DESC_LIMITS.short.maxChars}
                />
                {errors.shortDescription && (
                  <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 600 }}>⚠ {errors.shortDescription}</span>
                )}
              </div>

              {/* ── Full Description ── */}
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Full Description <span style={{ color: KM.muted, textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(min {DESC_LIMITS.long.minWords} words)</span></span>
                  <span style={{ fontSize: 11, color: (() => { const w = countWords(formData.fullDescription); return (w > 0 && w < DESC_LIMITS.long.minWords) ? '#ef4444' : '#9ca3af'; })(), fontWeight: 500, textTransform: 'none' }}>
                    {formData.fullDescription.length}/{DESC_LIMITS.long.maxChars} chars · {countWords(formData.fullDescription)}/{DESC_LIMITS.long.minWords}+ words
                  </span>
                </label>
                <textarea
                  style={{
                    ...inputStyle,
                    minHeight: 80,
                    resize: 'vertical',
                    borderColor: errors.fullDescription ? '#ef4444' : undefined,
                    boxShadow: errors.fullDescription ? '0 0 0 2px rgba(239,68,68,0.1)' : undefined,
                  }}
                  value={formData.fullDescription}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData({ ...formData, fullDescription: val });
                    const wc = countWords(val);
                    if (val && wc < DESC_LIMITS.long.minWords) {
                      setErrors(prev => ({ ...prev, fullDescription: `Long description must be at least ${DESC_LIMITS.long.minWords} words (currently ${wc})` }));
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.fullDescription; return n; });
                    }
                  }}
                  placeholder="Materials, dimensions, customisation details, delivery time…"
                  maxLength={DESC_LIMITS.long.maxChars}
                />
                {errors.fullDescription && (
                  <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 600 }}>⚠ {errors.fullDescription}</span>
                )}
              </div>

              <div ref={variantBuilderRef} style={{ gridColumn: 'span 2' }}>
                <VariantBuilder variants={variants} errors={errors.variantErrors || []} tab={variantTab} onTabChange={setVariantTab} onChange={(updated) => {
                  setVariants(updated);
                  const first = updated[0];
                  if (first?.mrp && first?.salesPrice && Number(first.mrp) > 0) {
                    const auto = Math.round((1 - Number(first.salesPrice) / Number(first.mrp)) * 100);
                    if (auto >= 0 && auto <= 100) setFormData(f => ({ ...f, discount: String(auto) }));
                  }
                }} />
              </div>
              <div style={{ gridColumn: 'span 2', marginTop: -10 }}>
                <ErrorMsg field="variants" />
              </div>

              {/* ── GST Rate + Inclusive Price Preview ── */}
              <div style={{
                gridColumn: 'span 2',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
                padding: '14px 16px',
                background: '#fff',
                border: `1px solid ${KM.border}`,
                borderRadius: 10,
                marginBottom: 0,
              }}>
                {/* GST % input */}
                <div style={fieldStyle}>
                  <label style={labelStyle}>GST Rate (%)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...inputStyle, paddingRight: 44 }}
                      type="number" min="0" max="100" step="0.01"
                      value={formData.gstRate}
                      placeholder="e.g. 5, 12, 18, 28"
                      onChange={e => setFormData({ ...formData, gstRate: e.target.value })}
                    />
                    <span style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 12, fontWeight: 700, color: KM.muted,
                    }}>%</span>
                  </div>
                  <span style={{ fontSize: 11, color: KM.muted, marginTop: 2 }}>
                    Sales price is inclusive of GST (GST is extracted from within)
                  </span>
                </div>

                {/* Live preview panel */}
                <div style={{ ...fieldStyle, justifyContent: 'center' }}>
                  {(() => {
                    const inclusivePrice = Number(variants[0]?.salesPrice);
                    const gst = Number(formData.gstRate);
                    if (!inclusivePrice || inclusivePrice <= 0) return (
                      <div style={{
                        padding: '10px 14px', background: KM.bg,
                        border: `1px solid ${KM.border}`, borderRadius: 8,
                        fontSize: 12, color: KM.muted,
                      }}>Enter a variant sales price to see the GST preview</div>
                    );
                    // Inclusive GST: GST is already inside the sales price
                    const gstAmt  = gst > 0 ? (inclusivePrice * gst) / (100 + gst) : 0;
                    const basePrice = inclusivePrice - gstAmt;
                    return (
                      <div style={{
                        padding: '10px 14px',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e8f5e9 100%)',
                        border: `1px solid #b2dfdb`,
                        borderRadius: 8,
                        display: 'flex', flexDirection: 'column', gap: 6,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: KM.blue, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                          GST Price Breakdown (Inclusive)
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: KM.text }}>
                          <span style={{ color: KM.muted }}>Inclusive Price (entered)</span>
                          <span style={{ fontWeight: 600 }}>₹{inclusivePrice.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: KM.text }}>
                          <span style={{ color: KM.muted }}>GST ({gst || 0}%) extracted</span>
                          <span style={{ fontWeight: 600, color: '#e67e22' }}>− ₹{gstAmt.toFixed(2)}</span>
                        </div>
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: 14, fontWeight: 700, color: KM.green,
                          borderTop: `1px solid #b2dfdb`, paddingTop: 6, marginTop: 2,
                        }}>
                          <span>Base Price (excl. GST)</span>
                          <span>₹{basePrice.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* ── Final Discount ── */}
              <div style={{
                gridColumn: 'span 2',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
                padding: '14px 16px',
                background: '#fff',
                border: `1px solid ${KM.border}`,
                borderRadius: 10,
              }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Final Discount (%)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...inputStyle, paddingRight: 80 }}
                      type="number" min="0" max="100"
                      value={formData.discount}
                      placeholder="0"
                      onChange={e => {
                        const newDiscount = e.target.value;
                        setFormData({ ...formData, discount: newDiscount });
                        const discVal = Number(newDiscount);
                        if (!Number.isNaN(discVal) && discVal >= 0 && discVal <= 100) {
                          setVariants(prev => prev.map(v => {
                            const mrpVal = Number(v.mrp);
                            if (mrpVal > 0) {
                              const newSalesPrice = (mrpVal * (1 - discVal / 100)).toFixed(2);
                              return { ...v, salesPrice: String(newSalesPrice) };
                            }
                            return v;
                          }));
                        }
                      }}
                    />
                    <ErrorMsg field="discount" />
                    {formData.discount > 0 && (
                      <span style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 11, fontWeight: 700, color: KM.orange,
                        background: KM.orangeLight, padding: '2px 8px', borderRadius: 4,
                      }}>{formData.discount}% OFF</span>
                    )}
                  </div>
                </div>
                <div style={{ ...fieldStyle, justifyContent: 'flex-end' }}>
                  {(() => {
                    const mrp = Number(variants[0]?.mrp);
                    const salesPrice = Number(variants[0]?.salesPrice);
                    if (!mrp || !salesPrice) return null;
                    const disc = Number(formData.discount);
                    return (
                      <div style={{
                        padding: '9px 12px', background: KM.bg,
                        border: `1px solid ${KM.border}`, borderRadius: 8,
                        fontSize: 13, color: KM.text, display: 'flex', gap: 10, alignItems: 'center',
                      }}>
                        <span style={{ color: KM.muted, fontSize: 11, textDecoration: 'line-through' }}>₹{mrp}</span>
                        <span style={{ fontWeight: 700, color: KM.green }}>₹{salesPrice.toFixed(2)}</span>
                        <span style={{ fontSize: 11, color: KM.orange, fontWeight: 600 }}>Save {disc}%</span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* ── Shipping Details (for Shiprocket) ── */}
              <div style={{
                gridColumn: 'span 2',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14,
                padding: '14px 16px',
                background: '#fff',
                border: `1px solid ${KM.border}`,
                borderRadius: 10,
              }}>
                <div style={{ gridColumn: 'span 4', fontSize: 12, fontWeight: 700, color: KM.blue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  📦 Shipping & Dimensions (For Shiprocket)
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Weight (kg)</label>
                  <input
                    style={inputStyle}
                    type="number" step="0.001" min="0.001"
                    value={formData.shippingWeight || ''}
                    placeholder="0.5"
                    onChange={e => setFormData({ ...formData, shippingWeight: e.target.value })}
                  />
                  <ErrorMsg field="shippingWeight" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Length (cm)</label>
                  <input
                    style={inputStyle}
                    type="number" step="1" min="1"
                    value={formData.shippingLength || ''}
                    placeholder="10"
                    onChange={e => setFormData({ ...formData, shippingLength: e.target.value })}
                  />
                  <ErrorMsg field="shippingLength" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Breadth (cm)</label>
                  <input
                    style={inputStyle}
                    type="number" step="1" min="1"
                    value={formData.shippingBreadth || ''}
                    placeholder="10"
                    onChange={e => setFormData({ ...formData, shippingBreadth: e.target.value })}
                  />
                  <ErrorMsg field="shippingBreadth" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Height (cm)</label>
                  <input
                    style={inputStyle}
                    type="number" step="1" min="1"
                    value={formData.shippingHeight || ''}
                    placeholder="10"
                    onChange={e => setFormData({ ...formData, shippingHeight: e.target.value })}
                  />
                  <ErrorMsg field="shippingHeight" />
                </div>
              </div>

              <div style={{ gridColumn: 'span 2', display: 'flex', gap: 20, flexWrap: 'wrap', padding: '12px 14px', background: KM.orangeLight, borderRadius: 8 }}>
                {[
                  { key: 'isCustomisable', label: '🎨 Customisable' },
                  { key: 'isNewArrival', label: '✨ New Arrival' },
                  { key: 'isHotDeal', label: '🔥 Hot Deal' },
                  { key: 'isPartialCodAvailable', label: '💳 Allow Partial COD' },
                ].map(item => (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                    <input type="checkbox" checked={!!formData[item.key]}
                      onChange={e => setFormData({ ...formData, [item.key]: e.target.checked })} />
                    {item.label}
                  </label>
                ))}
              </div>

              {/* Customisation Fields — visible only when Customisable is checked */}
              {formData.isCustomisable && (
                <div style={{ gridColumn: 'span 2', padding: '12px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Customisation Input Fields</div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {customisationFieldsTemplates.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#166534', opacity: 0.8 }}>No active customisation fields found. Add them in Settings → Customisation Fields first.</div>
                    ) : (
                      customisationFieldsTemplates.map(field => (
                        <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500, color: '#166534' }}>
                          <input type="checkbox"
                            checked={!!(formData.customisationFields?.[field.key])}
                            onChange={e => setFormData({
                              ...formData,
                              customisationFields: { ...formData.customisationFields, [field.key]: e.target.checked }
                            })} />
                          {field.icon ? <span style={{ marginRight: 2 }}>{field.icon}</span> : null}
                          {field.label} {field.isRequired ? <span style={{ color: '#ef4444' }}>*</span> : null}
                        </label>
                      ))
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#166534', marginTop: 8 }}>Selected fields will appear as input fields on the product page for customers to fill in.</div>
                </div>
              )}

              {/* Hidden file input - product images handled per-variant */}
              <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/x-icon,image/heic,image/heif,image/avif"
                style={{ display: 'none' }} onChange={handleFileChange} />

              <div className="km-form-actions km-field-full" style={{display : "flex",gap : "10px"}}>
                <button type="submit" className="km-btn-submit">
                  {editingId ? 'Update Product' : 'Save Product'}
                </button>
                {editingId && (
                  <button type="button" className="km-btn-cancel" onClick={handleCancel}
                    >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <p style={{ padding: 20 }}>Loading Products...</p> : (
        <DataTable
          columns={(() => {
            const cols = ['No.', 'Gallery', 'Product', 'Category', 'Sub-Cat', 'Price', 'Disc', 'Stock', 'Variants', 'Flags'];
            if (hasPermission('products_edit') || hasPermission('products_delete')) cols.push('Actions');
            return cols;
          })()}
          initialRows={rows}
          renderRow={(row, i) => {
            const firstVar = row.Variants?.[0];
            const stock = totalStock(row);
            const productImgs = row.images || (row.image ? (Array.isArray(row.image) ? row.image : [row.image]) : []);
            const variantImgs = (row.Variants || []).flatMap(v => {
              if (!v.image) return [];
              if (Array.isArray(v.image)) return v.image;
              if (typeof v.image === 'string' && v.image.trim().startsWith('[')) {
                try {
                  const parsed = JSON.parse(v.image);
                  return Array.isArray(parsed) ? parsed : [v.image];
                } catch {
                  return [v.image];
                }
              }
              return [v.image];
            }).filter(Boolean);
            const imgs = productImgs.length ? productImgs : variantImgs;
            return (
              <tr key={row.id}>
                <td style={{ color: KM.muted, fontSize: 11 }}>{i + 1}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {imgs.slice(0, 3).map((img, idx) => (
                      <img key={idx} src={getImageUrl(img)} alt=""
                        width="36" height="36"
                        style={{ borderRadius: 7, objectFit: 'cover', border: `1px solid ${KM.border}` }} />
                    ))}
                    {imgs.length > 3 && (
                      <div style={{
                        width: 36, height: 36, borderRadius: 7,
                        background: KM.bg, border: `1px solid ${KM.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: KM.muted, fontWeight: 600,
                      }}>+{imgs.length - 3}</div>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{row.name}</div>
                  {row.shortDescription && (
                    <div style={{ fontSize: 11, color: KM.muted, marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.shortDescription}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 12, fontWeight: 500 }}>
                  {row.Category?.name || row.Category?.label || '—'}
                </td>
                <td style={{ fontSize: 12, color: KM.muted, textTransform: "capitalize" }} className='text-capitalize'>
                  {row.SubCategory?.label || row.subCategoryName || '—'}
                </td>
                <td style={{ fontWeight: 700 }}>₹{firstVar?.salesPrice ?? row.price ?? 0}</td>
                <td>
                  {row.discount > 0
                    ? <span style={tag(KM.orange, KM.orangeLight)}>{row.discount}% off</span>
                    : <span style={{ color: KM.muted }}>—</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: stockColor(stock) }} />
                    <span style={{ fontWeight: 700, color: stockColor(stock) }}>{stock}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 250, maxHeight: 60, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
                    {row.Variants?.slice(0, 5).map((v, j) => {
                      const attrs = Array.isArray(v.attributes) ? v.attributes : [];
                      const label = attrs.length
                        ? attrs.map(a => a.value).filter(Boolean).join(' / ')
                        : [v.subCategory, v.colour, v.size, v.material].filter(Boolean).join(' / ')
                        || v.variantName || '—';
                      return (
                        <span key={j} title={label} style={{ 
                          fontSize: 10, 
                          padding: '3px 7px', 
                          borderRadius: 4, 
                          background: '#f0f4ff', 
                          color: KM.blue, 
                          border: `1px solid #c7d4f0`, 
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}>
                          {renderVariantLabel(label, 10, 3)}
                        </span>
                      );
                    })}
                    {row.Variants?.length > 5 && (
                      <span style={{ 
                        fontSize: 10, 
                        padding: '3px 7px', 
                        borderRadius: 4, 
                        background: '#f3f4f6', 
                        color: KM.muted, 
                        border: `1px solid #e5e7eb`, 
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}>
                        +{row.Variants.length - 5} more
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {row.isNew && <span style={tag(KM.green, '#ecfdf5')}>NEW</span>}
                    {row.isCustomisable && <span style={tag(KM.teal, '#ecfeff')}>CUSTOM</span>}
                    {row.isHotDeal && <span style={tag(KM.orange, KM.orangeLight)}>HOT DEAL</span>}
                  </div>
                </td>
                {(hasPermission('products_edit') || hasPermission('products_delete')) && (
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {hasPermission('products_edit') && (
                        <button className="action-btn btn-edit" onClick={() => handleEdit(row)}>Edit</button>
                      )}
                      {hasPermission('products_delete') && (
                        <button className="action-btn btn-del" onClick={() => handleDelete(row.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          }}
        />
      )}
    </div>
  );
}